import styled from '@emotion/styled';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Box from '~/common/components/Box';
import Checkbox from '~/common/components/Checkboxes/Checkbox/Checkbox';
import Stack from '~/common/components/Stacks/Stack';
import Text from '~/common/components/Texts/Text';
import { RULE_ID } from '~/common/constants';
import { useUser } from '~/common/hooks/useAuthentication';
import useBoltStatus from '~/common/hooks/useBoltStatus';
import useFullPageWithPDFViewer from '~/common/hooks/useFullPageWithPDFViewer';
import { isVisible } from '~/common/util/underwriter';
import { CATEGORY_IDS } from '~/pages/Home/constants';
import { IncomeRuleIds } from '~/pages/IncomeCalculator/enums';
import { useNavigation } from '~/stores/navigationStore';

import { setUnderwriterVerifiedRule } from './redux/action';

const getBoxStyle = (showHeader: boolean) => ({
  borderStyle: 'solid',
  borderWidth: '1px',
  borderColor: 'blue.500',
  borderRadius: '4px',
  bg: 'blue.50',
  width: '100%',
  height: showHeader ? '89px' : '55px',
});

const StyledBox = styled<any>(Box)((props) => ({
  pointerEvents: props.isWriteAccessDisabled ? 'none' : 'auto',
  opacity: props.isWriteAccessDisabled ? 0.6 : 1,
  padding: props.isFullPageWithPDFViewer ? '1rem 1rem 1rem' : '1rem 0 1rem',
  borderTop: `1px solid ${props.theme.colors?.gray[100]}`,
}));

export type UnderwriterCheckboxProps = {
  readonly header?: string | null;
  readonly text?: string;
  readonly isHidden?: boolean;
  readonly isDisabled?: boolean;
};

const UnderwriterCheckbox = ({
  header = 'Verified by Underwriter',
  text = 'Approve',
  isHidden = false,
  isDisabled = false,
}: UnderwriterCheckboxProps | undefined) => {
  const dispatch = useDispatch();
  const [isVerified, setIsVerified] = useState(null);
  const { isFullPageWithPDFViewer } = useFullPageWithPDFViewer();
  const { selectedAlert, selectedCategory } = useNavigation();
  const isUnderwriterCheckboxDisabled = useSelector(
    (state) => state.home.isUnderwriterCheckboxDisabled,
  );
  const { isUnderwriter } = useUser();
  const {
    data: { isLoanZapped, isLoanSubmitted },
  } = useBoltStatus();

  const ruleId = selectedAlert?.ruleId;
  const { canWriteUnderwriterCheckbox, hasAccessToUnderwriterView } = useUser();
  const showHeader = header !== null;

  const handleOnChangeFeVerified = ({ target }) => {
    const checked = target.checked;
    setIsVerified(checked);
    dispatch(setUnderwriterVerifiedRule(checked));
  };

  useEffect(() => {
    if (
      selectedCategory?.navigationCategoryId === CATEGORY_IDS.CLASSIFY ||
      (selectedCategory?.navigationCategoryId === CATEGORY_IDS.INCOME &&
        ruleId === IncomeRuleIds.NoDocumentationProvidedIncomeSummary)
    )
      return; // skip if the category is classify or income

    let underwriterVerifiedRule = selectedAlert?.isUnderwriterVerified;

    if (
      ruleId === RULE_ID.ASSET_RECONCILIATION_RULE_ID ||
      ruleId === RULE_ID.ASSET_TYPE_RULE_ID ||
      ruleId === RULE_ID.ASSET_ACCOUNT_REVIEW_RULE_ID ||
      (ruleId === RULE_ID.NEW_CONSTRUCTION_RULE_ID && isLoanZapped)
    ) {
      underwriterVerifiedRule = true; // override the value
    }
    setIsVerified(underwriterVerifiedRule);
    dispatch(setUnderwriterVerifiedRule(underwriterVerifiedRule));
  }, [selectedAlert, selectedCategory, ruleId, isLoanZapped, dispatch]);

  const showCheckbox = useMemo(() => {
    return (
      !isHidden &&
      isVisible(
        isUnderwriter,
        selectedAlert,
        isLoanSubmitted,
        isLoanZapped,
        hasAccessToUnderwriterView,
      )
    );
  }, [
    isHidden,
    isUnderwriter,
    selectedAlert,
    isLoanSubmitted,
    isLoanZapped,
    hasAccessToUnderwriterView,
  ]);

  const isDisabledInternal =
    isDisabled || isUnderwriterCheckboxDisabled || !canWriteUnderwriterCheckbox;

  return (
    showCheckbox && (
      <StyledBox
        isWriteAccessDisabled={isDisabledInternal}
        isFullPageWithPDFViewer={isFullPageWithPDFViewer}
        padding="1rem"
      >
        <Box {...getBoxStyle(showHeader)} data-testid="underwriter-checkbox">
          <Stack spacing="10px" ml="27px" mt="15px">
            {showHeader && (
              <Text fontSize=".875rem" fontWeight="600">
                {header}
              </Text>
            )}
            <Checkbox
              fontSize=".75rem"
              fontWeight="400"
              onChange={handleOnChangeFeVerified}
              isChecked={isVerified}
              isDisabled={isDisabledInternal}
            >
              {text}
            </Checkbox>
          </Stack>
        </Box>
      </StyledBox>
    )
  );
};

export default UnderwriterCheckbox;
